import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emptyLocation = { name: "", address: "", lat: "", lng: "" };

export default function Locations() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(emptyLocation);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // location id

  const load = () => api.get("/locations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(emptyLocation); };
  const openEdit = (l) => {
    setEditing(l.id);
    setForm({
      name: l.name,
      address: l.address || "",
      lat: l.lat != null ? String(l.lat) : "",
      lng: l.lng != null ? String(l.lng) : "",
    });
  };
  const close = () => setEditing(null);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Store name is required");
      return;
    }
    // Coerce lat/lng — only send when BOTH provided as valid numbers
    const latNum = form.lat === "" ? null : Number(form.lat);
    const lngNum = form.lng === "" ? null : Number(form.lng);
    if ((form.lat !== "" || form.lng !== "") && (Number.isNaN(latNum) || Number.isNaN(lngNum))) {
      toast.error("Latitude and longitude must be numbers");
      return;
    }
    if (latNum !== null && (latNum < -90 || latNum > 90)) { toast.error("Latitude must be between -90 and 90"); return; }
    if (lngNum !== null && (lngNum < -180 || lngNum > 180)) { toast.error("Longitude must be between -180 and 180"); return; }
    const payload = { name: form.name, address: form.address };
    if (latNum !== null && lngNum !== null) { payload.lat = latNum; payload.lng = lngNum; }
    setBusy(true);
    try {
      const { data } =
        editing === "new"
          ? await api.post("/locations", payload)
          : await api.put(`/locations/${editing}`, payload);
      await load();
      if (form.address.trim() && data.lat == null && latNum === null) {
        toast.warning("Saved, but the address couldn't be located on the map. Enter latitude & longitude manually or try a more complete address.", { duration: 8000 });
      } else {
        toast.success("Store saved");
      }
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  const retryGeocode = async (id) => {
    try {
      const { data } = await api.post(`/locations/${id}/geocode`);
      await load();
      if (data.lat != null) toast.success(`Coordinates resolved: ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`);
      else toast.warning("Couldn't resolve automatically — please add lat/lng manually.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/locations/${id}`);
      setItems((prev) => prev.filter((l) => l.id !== id));
      toast.success("Store deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Locations"
        title="Stores"
        description="Define the stores where your products are available. Assign them to products in the catalog — the kiosk will show customers the closest store carrying their recommended product."
      >
        <Button
          onClick={openNew}
          data-testid="add-location-button"
          className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
        >
          <Plus className="mr-2 w-4 h-4" /> Add store
        </Button>
      </PageHeader>

      <div className="p-10">
        <div className="bg-white border border-[#e5e5e5]">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-[#e5e5e5] items-center">
            <div className="col-span-4 label-overline">Store</div>
            <div className="col-span-5 label-overline">Address</div>
            <div className="col-span-2 label-overline">Map status</div>
            <div className="col-span-1 label-overline text-right">Actions</div>
          </div>
          {items.length === 0 && (
            <div className="px-6 py-16 text-center text-sm text-[#52525b]" data-testid="empty-locations">
              No stores yet. Add your first store with its address — we'll pin it on the map automatically
              so customers can see the closest store for their recommended product.
            </div>
          )}
          {items.map((l) => (
            <div
              key={l.id}
              className="grid grid-cols-12 items-center px-6 py-4 border-b border-[#e5e5e5] last:border-b-0 hover:bg-[#fafafa] transition-colors"
              data-testid={`location-row-${l.id}`}
            >
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-[#fafafa] border border-[#e5e5e5] flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#52525b]" />
                </div>
                <div className="font-medium truncate">{l.name}</div>
              </div>
              <div className="col-span-5 text-sm text-[#52525b] truncate">{l.address || "—"}</div>
              <div className="col-span-2">
                {l.lat != null ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#2f7d4f]" data-testid={`geo-ok-${l.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2f7d4f]" /> Pinned
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#b45309]" data-testid={`geo-missing-${l.id}`}>
                    <AlertTriangle className="w-3 h-3" /> No coordinates
                  </span>
                )}
              </div>
              <div className="col-span-1 flex justify-end gap-2">
                {l.lat == null && (
                  <button
                    onClick={() => retryGeocode(l.id)}
                    data-testid={`retry-geocode-${l.id}`}
                    className="p-2 hover:bg-white border border-[#e5e5e5] text-[#b45309]"
                    title="Retry auto-geocoding"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => openEdit(l)}
                  data-testid={`edit-location-${l.id}`}
                  className="p-2 hover:bg-white border border-[#e5e5e5]"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setConfirmDel(l.id)}
                  data-testid={`delete-location-${l.id}`}
                  className="p-2 hover:bg-white border border-[#e5e5e5] text-[#e63946]"
                  title="Delete store"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Sheet open={editing !== null} onOpenChange={(o) => !o && close()}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none border-l border-[#e5e5e5] flex flex-col p-0" data-testid="location-sheet">
          <SheetHeader className="shrink-0 p-6 pb-4 border-b border-[#e5e5e5]">
            <SheetTitle className="font-display font-bold tracking-tight text-2xl">
              {editing === "new" ? "Add store" : "Edit store"}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
            <div>
              <Label className="label-overline">Store name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Downtown Flagship"
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="location-name"
              />
            </div>
            <div>
              <Label className="label-overline">Address</Label>
              <Textarea
                rows={3}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="12 Rue de Rivoli, 75001 Paris, France"
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="location-address"
              />
              <p className="mt-1 text-xs text-[#52525b]">
                We geocode this automatically so the kiosk can compute the closest store to each customer.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Latitude</Label>
                <Input
                  inputMode="decimal"
                  value={form.lat}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })}
                  placeholder="19.2472"
                  className="mt-2 rounded-none border-[#e5e5e5] font-mono"
                  data-testid="location-lat"
                />
              </div>
              <div>
                <Label className="label-overline">Longitude</Label>
                <Input
                  inputMode="decimal"
                  value={form.lng}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })}
                  placeholder="72.9819"
                  className="mt-2 rounded-none border-[#e5e5e5] font-mono"
                  data-testid="location-lng"
                />
              </div>
              <p className="col-span-2 text-xs text-[#52525b]">
                Optional. Fill these in when the address alone can't be resolved on the map — you'll find
                them by right-clicking a spot in Google Maps and copying the "lat, long" pair.
              </p>
            </div>
          </div>

          <div className="shrink-0 bg-white border-t border-[#e5e5e5] p-6 flex justify-end gap-3">
            <Button variant="outline" onClick={close} className="rounded-none h-11 px-5 border-[#e5e5e5]">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="save-location-button"
              className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="rounded-none border border-[#e5e5e5]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-bold tracking-tight text-2xl">
              Delete this store?
            </AlertDialogTitle>
            <AlertDialogDescription>
              It will be removed from all products that reference it. This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-delete-location-cancel"
              className="rounded-none border-[#e5e5e5] h-11 px-5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-location-confirm"
              onClick={() => { remove(confirmDel); setConfirmDel(null); }}
              className="rounded-none bg-[#e63946] hover:bg-[#c1303d] text-white h-11 px-5"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
